/**
 * The TMY estimate: one run, kept, read.
 *
 * The two workbook buttons used to run the whole annual simulation each - twice
 * the work for one answer, and two files that could describe different systems
 * if a setting changed between the clicks. Now a single Estimate produces both
 * workbooks and the numbers on this page, and the buttons are downloads.
 *
 * What is shown is chosen to answer the questions a reader actually has about a
 * yield run, in the order they ask them: how much energy, how good is that for
 * this plant, when does it arrive, and where did the rest of the sunlight go.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useLanguage } from "../context/LanguageContext";
import PvLoadingRegion from "./pv/PvLoadingRegion";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  downloadTmyWorkbook,
  downloadTmyReport,
  fetchConfigFingerprint,
  fetchTmyEstimate,
  runTmyEstimate,
} from "../services/api";
import { getRechartsTooltipProps } from "../theme";
import OrientationYieldTable from "./pv/OrientationYieldTable";
import { estimateMetricHints, estimateMonthName, monthlyEnergyTooltipLabel } from "./pv/estimatePresentation";
import { shortYieldLabel, yieldLabel } from "../i18n/pvYieldLabels";
import useTmyEstimationJob from "./pv/useTmyEstimationJob";
import EstimationProgress from "./pv/EstimationProgress";
import LayoutShadingDialog from "./pv/LayoutShadingDialog";
import { sceneShadingPending } from "./config/sceneSimulationIssues";
import { configWithoutLayoutShading } from "../utils/runWithoutLayoutShading";
import { scenePreparationMessage } from "../utils/scenePreparationIssue";
import { isSceneCalculationCancelled } from "../services/sceneCalculation";
import { explainMissingPvLossValue, missingDynamicDcWiring } from "./config/pvWiringReadiness";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_EL = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν",
                   "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];

const nf = (value, digits = 0) =>
  value == null || Number.isNaN(Number(value))
    ? "—"
    : Number(value).toLocaleString("en-GB", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });

/** One offset written out: "UTC+2". */
const utcLabel = (offset) =>
  offset == null || Number.isNaN(Number(offset))
    ? null
    : `UTC${Number(offset) >= 0 ? "+" : "\u2212"}${nf(Math.abs(Number(offset)), Number(offset) % 1 ? 1 : 0)}`;

/**
 * Which clock the hour stamps are on: "UTC+2 winter / UTC+3 summer".
 *
 * The stamps follow the site's clock, so at a site that keeps summer time there
 * are two offsets and both belong on the label - naming one of them would be
 * the same ambiguity as saying "local time" and leaving it there.
 */
function clockLabel(timeBase, isGreek = false) {
  const winter = utcLabel(timeBase?.utc_offset_hours);
  if (!winter) return null;
  const summer = utcLabel(timeBase?.summer_offset_hours);
  // Built here rather than left to the DOM translation bridge: the bridge
  // replaces whole text nodes, and every one of these carries an offset in the
  // middle of it, so there is no static sentence for it to match on.
  if (summer) {
    return isGreek
      ? `${winter} χειμώνα / ${summer} καλοκαίρι`
      : `${winter} winter / ${summer} summer`;
  }
  return isGreek ? `${winter} όλο το έτος` : `${winter} all year`;
}

function timeBaseTooltip(timeBase, isGreek) {
  if (!isGreek || !timeBase?.note) return timeBase?.note || "";
  const rows = nf(timeBase.rows);
  const winter = utcLabel(timeBase.utc_offset_hours);
  const summer = utcLabel(timeBase.summer_offset_hours);
  if (!winter) return `Οι χρονικές ενδείξεις ακολουθούν την τοπική ώρα της εγκατάστασης. Το έτος περιλαμβάνει ${rows} ωριαίες εγγραφές.`;
  if (!summer) return `Οι χρονικές ενδείξεις είναι ${winter} όλο το έτος, χωρίς αλλαγή σε θερινή ώρα. Το έτος περιλαμβάνει ${rows} ωριαίες εγγραφές.`;
  return `Οι χρονικές ενδείξεις ακολουθούν την τοπική ώρα: ${winter} τον χειμώνα και ${summer} το καλοκαίρι. Η προσομοίωση χρησιμοποιεί σταθερή χρονική βάση και τα αποτελέσματα εμφανίζονται στην τοπική ώρα. Παραμένουν ${rows} ωριαίες εγγραφές: η ώρα της ανοιξιάτικης αλλαγής παραλείπεται και η ώρα της φθινοπωρινής αλλαγής εμφανίζεται δύο φορές. Η στήλη μετατόπισης UTC στα αρχεία τις διακρίνει.`;
}

/** One headline number with its unit and a line saying what it means. */
function Metric({ label, value, unit, hint }) {
  const { translateText } = useLanguage();
  const body = (
    <Box sx={{ minWidth: 0, height: "100%", boxSizing: "border-box", px: 1.75, py: 1.5,
      border: "1px solid", borderColor: (theme) => alpha(theme.palette.primary.main, 0.13),
      borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
      borderTop: "3px solid", borderTopColor: (theme) => alpha(theme.palette.primary.main, 0.35) }}>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
        {translateText(label)}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {value}
        {unit ? (
          <Typography component="span" variant="body2" sx={{ ml: 0.4, color: "text.secondary" }}>
            {unit}
          </Typography>
        ) : null}
      </Typography>
    </Box>
  );
  return hint ? <Tooltip arrow title={translateText(hint)}>{body}</Tooltip> : body;
}

/**
 * Hour of day against day of year, drawn on a canvas.
 *
 * 8,760 cells is too many for SVG rectangles and exactly what a canvas is for.
 * The shape carries what a table cannot: the summer plateau of a clipping
 * inverter reads as a flat top, morning row shading as a bitten-off left edge.
 */
function Heatmap({ values, peak }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !Array.isArray(values) || values.length === 0) return;
    const days = values.length;
    const hours = values[0].length;
    const scale = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 720;
    const height = 190;
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.clearRect(0, 0, width, height);
    const cellW = width / days;
    const cellH = height / hours;
    const top = peak || Math.max(...values.flat(), 1);
    for (let d = 0; d < days; d += 1) {
      for (let h = 0; h < hours; h += 1) {
        const v = Math.max(0, Number(values[d][h]) || 0) / top;
        // Night stays the page's own ground rather than becoming a colour of
        // its own: what matters is where power is, not where it is not.
        ctx.fillStyle = v <= 0.001
          ? "rgba(148,163,184,0.13)"
          : `hsl(${42 - 34 * v}deg 92% ${88 - 44 * v}%)`;
        ctx.fillRect(d * cellW, (hours - 1 - h) * cellH, Math.ceil(cellW), Math.ceil(cellH));
      }
    }
  }, [values, peak]);
  return <canvas ref={ref} style={{ width: "100%", height: 190, display: "block" }} />;
}

export default function PvTmyEstimate({
  reportBranding,
  projectId,
  config,
  accessToken,
  projectName = null,
  disabled = false,
  disabledReason = "",
  onWeather = null,
  active = true,
  // Calculates the layout's shading and stores it with the layout
  // (({ onProgress, shouldCancel }) => Promise<config>), for the question
  // Estimate asks when the layout's shading is out of date.
  onCalculateShading = null,
  onOpenPvLosses = null,
}) {
  const rechartsTooltipProps = getRechartsTooltipProps(useTheme().palette.mode);
  const { isGreek, translateText } = useLanguage();
  const [estimate, setEstimate] = useState(null);
  const [loadedScope, setLoadedScope] = useState(null);
  const restoring = Boolean(projectId && (loadedScope?.projectId !== projectId || loadedScope?.accessToken !== accessToken));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Which downloads are in flight, not which one. They are four independent
  // GETs that share nothing -- each builds its own blob and its own link -- so
  // one of them running is no reason to bar the other three. A single string
  // also lost the second button's label: the first to finish cleared it.
  const [downloading, setDownloading] = useState(() => []);
  const [fingerprint, setFingerprint] = useState(null);
  const [profileMonth, setProfileMonth] = useState(null);
  const [jobError, setJobError] = useState("");
  // A completed run supersedes any initial read still in flight for this page.
  const estimateRevision = useRef(0);
  const acceptRunEstimate = useCallback((value) => {
    estimateRevision.current += 1;
    setEstimate(value);
    setLoadedScope({ projectId, accessToken });
  }, [projectId, accessToken]);
  const estimationJob = useTmyEstimationJob({
    enabled: Boolean(projectId),
    projectId, accessToken, onComplete: acceptRunEstimate, onError: setJobError,
  });
  const estimating = loading || estimationJob.pending;

  // What is stored, if anything. A project that has never run one is not an
  // error state; it is the normal starting state.
  useEffect(() => {
    let cancelled = false;
    const revision = estimateRevision.current;
    setLoadedScope(null);
    if (!projectId) {
      setEstimate(null);
      return undefined;
    }
    fetchTmyEstimate(projectId, accessToken)
      .then((data) => { if (!cancelled && revision === estimateRevision.current) setEstimate(data); })
      .catch(() => { if (!cancelled && revision === estimateRevision.current) setEstimate(null); })
      .finally(() => { if (!cancelled) setLoadedScope({ projectId, accessToken }); });
    return () => { cancelled = true; };
  }, [projectId, accessToken]);

  const storedFingerprint = estimate?.config_fingerprint;

  // The fingerprint of the settings in hand, so a stored estimate can say
  // whether it still describes them.
  useEffect(() => {
    let cancelled = false;
    if (!config || !active || !projectId) return undefined;
    setFingerprint(null);
    fetchConfigFingerprint(config, accessToken, storedFingerprint, projectId)
      .then((value) => { if (!cancelled) setFingerprint(value); })
      .catch(() => { if (!cancelled) setFingerprint(null); });
    return () => { cancelled = true; };
  }, [config, accessToken, storedFingerprint, projectId, active]);

  const stale = Boolean(
    estimate && fingerprint && estimate.config_fingerprint &&
    estimate.config_fingerprint !== fingerprint
  );

  // The layout's shading missing or out of date: Estimate asks (a popup)
  // instead of working it out in the browser on the way, unsaved, on every run.
  const shadingPending = useMemo(() => Boolean(config) && sceneShadingPending(config).length > 0, [config]);
  const wiringIssues = useMemo(() => missingDynamicDcWiring(config), [config]);
  const [shadingQuestion, setShadingQuestion] = useState(false);

  // `config`: the project as it stands after "Calculate shading", which the
  // page has stored but this render has not received yet.
  const run = useCallback(async ({ withoutShading = false, config: current = null } = {}) => {
    if (disabled) {
      setError(disabledReason || (isGreek ? "Ελέγξτε τις εκκρεμότητες του σχεδιασμού πριν από την εκτίμηση." : "Resolve the design issues before estimating."));
      return;
    }
    setError("");
    setJobError("");
    setLoading(true);
    try {
      const base = current || config;
      const runConfig = withoutShading ? configWithoutLayoutShading(base) : base;
      if (missingDynamicDcWiring(runConfig).length) return;
      const result = await runTmyEstimate(projectId, runConfig, accessToken);
      if (result?.job) estimationJob.submitted(result.job);
      else acceptRunEstimate(result);
    } catch (exc) {
      if (exc?.code === "PV_DC_INVALID_OUTPUT") {
        const count = exc?.technicalDetail?.match(/count=(\d+)/)?.[1];
        const moduleName = config?.read_module ? ` (${config.read_module})` : "";
        setError(isGreek
          ? `Η εκτίμηση σταμάτησε επειδή το επιλεγμένο Φ/Β πλαίσιο${moduleName} έδωσε μη έγκυρες τιμές ισχύος DC${count ? ` σε ${count} ώρες` : ""}, ακόμη και μετά τον χειρισμό των ωρών χωρίς ακτινοβολία. Ελέγξτε τα στοιχεία του πλαισίου στον κατάλογο και αν η τεχνολογία του ταιριάζει στην εγκατάσταση.`
          : `The estimate stopped because the selected PV module${moduleName} produced invalid DC power${count ? ` in ${count} hours` : ""}, even after hours without irradiance were handled. Check its catalogue parameters and whether its technology suits the installation.`);
      } else if (isSceneCalculationCancelled(exc)) {
        // Cancelled by the user: nothing went wrong.
      } else if (exc?.sceneCode) {
        // A shading calculation that could not be made: its reason, in the
        // page's language, never the engine's own wording.
        setError(scenePreparationMessage(exc, isGreek));
      } else {
        setError(explainMissingPvLossValue(
          exc?.message || (isGreek ? "Δεν ήταν δυνατή η εκτίμηση." : "The estimate could not be run"), isGreek,
        ));
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, config, accessToken, isGreek, estimationJob, acceptRunEstimate, disabled, disabledReason]);

  const download = useCallback(async (kind) => {
    setError("");
    setDownloading((prev) => (prev.includes(kind) ? prev : [...prev, kind]));
    try {
      const { blob, filename } = kind === "report"
        ? await downloadTmyReport(projectId, config, projectName, accessToken, reportBranding)
        : await downloadTmyWorkbook(projectId, kind, accessToken);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (exc) {
      setError(exc?.message || "The download failed");
    } finally {
      setDownloading((prev) => prev.filter((entry) => entry !== kind));
    }
  }, [projectId, config, projectName, accessToken, reportBranding]);

  // The weather an estimate ran on, handed back so the page's own provenance
  // chip states it. It used to come from a download response header, which meant
  // it only appeared after somebody downloaded something.
  useEffect(() => {
    if (!onWeather || !estimate) return;
    const rows = estimate?.time_base?.rows;
    const clock = clockLabel(estimate?.time_base, isGreek);
    const rowsPhrase = isGreek
      ? `${nf(rows)} ωριαίες σειρές στην ώρα του έργου`
      : `${nf(rows)} hourly rows on the site clock`;
    const parts = [
      estimate?.weather?.description,
      rows ? rowsPhrase + (clock ? ` (${clock})` : "") : null,
    ].filter(Boolean);
    onWeather({
      label: estimate?.weather?.label || null,
      detail: parts.join(" · ") || null,
      // The identity of the dataset, not its name. The name is what the reader
      // sees; this is what the page compares against the current selection, and
      // a renamed or re-uploaded file would break a comparison on names.
      datasetId: estimate?.weather?.dataset_id ?? null,
      provider: estimate?.weather?.provider ?? null,
    });
  }, [estimate, onWeather, isGreek]);

  const monthly = useMemo(
    () => (estimate?.monthly || []).map((row) => ({
      ...row,
      name: (isGreek ? MONTHS_EL : MONTHS)[(row.month || 1) - 1],
      fullName: estimateMonthName(row.month, isGreek, true),
    })),
    [estimate, isGreek]
  );

  // The full ladder now opens at the horizontal resource, which is what makes it
  // readable against a PVsyst loss diagram -- and what makes it unusable as a
  // single bar chart: the optical rows are eight GWh of sunlight on the
  // collector area, the electrical ones about 1.3 GWh of delivered energy, and
  // drawn together the losses the chart exists to show become invisible.
  //
  // So the two halves are presented as what each is. The optical half is a table
  // of named deductions with percentages -- which is the form PVsyst uses, and
  // the form a reviewer reads line by line -- and the bars keep the electrical
  // half, where the magnitudes are comparable.
  const allSteps = useMemo(
    () => (estimate?.loss_steps || []).map((step) => ({
      ...step,
      short: shortYieldLabel(step.stage, isGreek),
      displayStage: yieldLabel(step.stage, isGreek),
      mwh: step.energy_kwh == null ? null : step.energy_kwh / 1000,
    })).filter((step) => step.mwh != null),
    [estimate, isGreek]
  );

  const conversionAt = useMemo(
    () => allSteps.findIndex((step) => step.kind === "conversion"),
    [allSteps]
  );
  const ladder = useMemo(
    () => (conversionAt >= 0 ? allSteps.slice(conversionAt) : allSteps),
    [allSteps, conversionAt]
  );
  // The opening level, which is a level and not a step, so it is not in
  // `allSteps` -- the service publishes those from rungs[1:].
  const opening = useMemo(() => {
    const rungs = estimate?.ladder || [];
    const first = rungs.length ? rungs[0] : null;
    return first && first.energy_kwh != null ? first : null;
  }, [estimate]);

  const opticalSteps = useMemo(
    () => (conversionAt > 0 ? allSteps.slice(0, conversionAt) : []),
    [allSteps, conversionAt]
  );

  // The busiest month, so the profile opens on the shape a reader wants to see
  // first: summer, where the clipping and the temperature losses live.
  const busiestMonth = useMemo(() => {
    let best = null;
    for (const row of estimate?.monthly || []) {
      if (best == null || (row.energy_mwh || 0) > (best.energy_mwh || 0)) best = row;
    }
    return best?.month || 6;
  }, [estimate]);

  const profileMonths = estimate?.daily_profile?.months || [];
  const activeMonth = profileMonth || busiestMonth;

  // One day, hour by hour: the month's average day, and its sunniest day. The
  // average is what a year of these months does; the sunniest is what the plant
  // can do, and where it stops.
  const profile = useMemo(() => {
    const entry = (estimate?.daily_profile?.months || []).find(
      (row) => row.month === activeMonth
    );
    if (!entry) return [];
    return (estimate?.daily_profile?.hours || []).map((hour, i) => ({
      hour,
      label: `${String(hour).padStart(2, "0")}`,
      mean: entry.mean_kw?.[i] ?? null,
      best: entry.best_kw?.[i] ?? null,
      poa: entry.poa_w_m2?.[i] ?? null,
    }));
  }, [estimate, activeMonth]);

  const m = estimate?.metrics || {};
  const metricHints = estimateMetricHints(m, isGreek);
  const offsetLabel = clockLabel(estimate?.time_base, isGreek);

  return (
    <PvLoadingRegion loading={restoring} isGreek={isGreek}>
    <Stack spacing={1.2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Tooltip
          arrow
          title={
            disabled && disabledReason
              ? translateText(disabledReason)
              : translateText("Uses the feasibility weather selection and study year. All results below come from the same first-year PV calculation.")
          }
        >
          <span>
            <Button
              variant="contained"
              onClick={() => (shadingPending ? setShadingQuestion(true) : run())}
              disabled={disabled || wiringIssues.length > 0 || estimating || !projectId}
              startIcon={estimating ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {/* One name, always: the button does the same thing whether or not
                  something is stored, and a label that changes underneath a
                  reader is a second thing to learn for no gain. */}
              {estimating ? "Estimating…" : "Estimate"}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          onClick={() => download("generation")}
          disabled={!estimate || downloading.includes("generation")}
        >
          {downloading.includes("generation") ? "Downloading…" : "PV generation (.xlsx)"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => download("losses")}
          disabled={!estimate || downloading.includes("losses") || estimate?.downloads?.losses === false}
        >
          {downloading.includes("losses") ? "Downloading…" : "PV losses (.xlsx)"}
        </Button>
        <Tooltip
          arrow
          title={translateText("The weather year this estimate ran on, hour by hour (GHI, DNI, DHI, air temperature, wind) with the site header, in the standard hourly layout other simulation tools import.")}
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              onClick={() => download("weather")}
              disabled={!estimate || downloading.includes("weather") || estimate?.downloads?.weather === false || estimate?.downloads?.weather === undefined}
            >
              {downloading.includes("weather") ? "Downloading…" : "Weather year (.csv)"}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          arrow
          title={
            stale
              ? translateText("This report describes the stored estimate, not the settings currently on the page. Run Estimate again to report the current design.")
              : translateText("The estimate as a document: the weather year, the plant, every loss from sunlight to delivered energy, month by month and hour by hour.")
          }
        >
          <span>
            {/* Changed settings no longer bar the download. The report is of
                the run that was made, which is still a real analysis and still
                the one the numbers on this page come from; the tooltip says
                which design it describes. The server refuses on its own for
                estimates stored before the plant recorded its own mounting,
                where the document would mix the old energy with the new
                design - and that refusal arrives as a readable message, which
                is better than a button that is dead with no explanation. */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => download("report")}
              disabled={!estimate || downloading.includes("report")}
            >
              {downloading.includes("report") ? "Preparing…" : "Generation report (.pdf)"}
            </Button>
          </span>
        </Tooltip>
        {estimate?.generated_at ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            last estimate {new Date(estimate.generated_at).toLocaleString("en-GB")}
          </Typography>
        ) : null}
      </Stack>

      <LayoutShadingDialog open={shadingQuestion} config={config} isGreek={isGreek}
        onClose={() => setShadingQuestion(false)}
        onCalculate={onCalculateShading}
        onContinue={(next) => { setShadingQuestion(false); run({ config: next }); }}
        onRunWithout={() => { setShadingQuestion(false); run({ withoutShading: true }); }}
        calculateLabel={isGreek ? "Υπολογισμός σκιάσεων και εκτίμηση" : "Calculate shading and estimate"}
        withoutLabel={isGreek ? "Εκτίμηση χωρίς σκιάσεις διάταξης" : "Estimate without layout shading"} />

      {/* The last estimate's bar stays in view after it finishes, on reload too:
          green and complete, it says the figures below come from a finished run. */}
      {(estimating || estimationJob.job?.status === "completed") && (
        <EstimationProgress job={loading ? null : estimationJob.job} isGreek={isGreek} />
      )}
      {disabled && disabledReason ? <Alert severity="info">{translateText(disabledReason)}</Alert> : null}
      {wiringIssues.length > 0 && <Alert severity="warning" action={onOpenPvLosses ? (
        <Button color="inherit" size="small" onClick={onOpenPvLosses}>
          {isGreek ? "Άνοιγμα Απωλειών ΦΒ" : "Open PV Losses"}
        </Button>
      ) : null}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {isGreek
            ? "Για να γίνει η εκτίμηση με δυναμική καλωδίωση DC, συμπληρώστε τα παρακάτω πεδία στις Απώλειες ΦΒ:"
            : "To estimate with dynamic DC wiring, complete these fields in PV Losses:"}
        </Typography>
        {wiringIssues.slice(0, 6).map(({ inverter, mppt, field, label }) => (
          <Typography key={`${inverter}-${mppt}-${field}`} variant="body2">
            {inverter} · {mppt} · {isGreek ? ({
              panelLinkLengthM: "Μήκος σύνδεσης πλαισίων",
              stringHomerunLengthM: "Μήκος γραμμής έως αντιστροφέα",
              panelCableSectionMm2: "Διατομή σύνδεσης πλαισίων",
              homerunCableSectionMm2: "Διατομή γραμμής έως αντιστροφέα",
            })[field] : label}
          </Typography>
        ))}
        {wiringIssues.length > 6 && <Typography variant="body2">
          {isGreek ? `και ${wiringIssues.length - 6} ακόμη πεδία` : `and ${wiringIssues.length - 6} more fields`}
        </Typography>}
        <Typography variant="caption">
          {isGreek
            ? "Χρησιμοποιήστε τα πραγματικά μήκη καλωδίων. Αν δεν είναι γνωστά ακόμη, μπορείτε να επιλέξετε στατική καλωδίωση και να δώσετε ποσοστιαία παραδοχή."
            : "Use measured cable lengths. If they are not known yet, select static wiring and enter an estimated percentage instead."}
        </Typography>
      </Alert>}
      {error ? <Alert severity="error">{translateText(error)}</Alert> : null}
      {estimationJob.job?.status === "failed" && jobError ? <Alert severity="error">
        {explainMissingPvLossValue(jobError, isGreek)}
      </Alert> : null}
      {estimationJob.connectionError && <Alert severity="warning">
        {isGreek ? "Δεν ήταν δυνατή η ενημέρωση της κατάστασης. Η σύνδεση θα επαναληφθεί αυτόματα." : "Could not refresh the estimation status. Reconnecting automatically."}
      </Alert>}

      {stale ? (
        <Alert severity="warning">
          These numbers are from an earlier version of this project&apos;s settings.
          Run Estimate again to bring them up to date.
        </Alert>
      ) : null}

      {!estimate && !estimating ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No estimate yet. Running one uses the feasibility weather selection for
          this design and keeps the result here, with both workbooks ready to download.
        </Typography>
      ) : null}

      {estimate ? (
        <>
          <Box sx={{ display: "grid", width: "100%", gap: 1.25, py: 0.5,
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: `repeat(${6 + (m.performance_ratio_bifacial != null ? 1 : 0) + (m.rear_gain_pct != null ? 1 : 0)}, minmax(0, 1fr))` } }}>
            <Metric label="Annual energy" value={nf(m.annual_energy_mwh, 1)} unit="MWh"
                    hint={metricHints.annual} />
            <Metric label="Specific yield" value={nf(m.specific_yield_kwh_kwp)} unit="kWh/kWp"
                    hint={metricHints.specific} />
            {/* On a bifacial plant the standard ratio runs high -- its
                denominator is the FRONT incident plane irradiance, which is not
                all the light the array converted -- and can pass 1.0, which
                reads as a model that does not know what a performance ratio is.
                Both are shown: the bifacial figure leads because it is the one
                that answers "is the plant performing", and the standard figure
                stays because it is the one a reviewer recomputes by hand and
                the one that compares to a monofacial benchmark. */}
            {m.performance_ratio_bifacial != null ? (
              <>
                <Metric label={isGreek ? "PR με οπίσθια συνεισφορά" : "PR including rear contribution"} value={nf(m.performance_ratio_bifacial, 3)}
                        hint={metricHints.bifacialPr} />
                <Metric label="PR, front-side basis" value={nf(m.performance_ratio, 3)}
                        hint={metricHints.frontPr} />
              </>
            ) : (
              <Metric label="Performance ratio" value={nf(m.performance_ratio, 3)}
                      hint={metricHints.pr} />
            )}
            {m.rear_gain_pct != null && (
              <Metric label="Rear-side gain" value={nf(m.rear_gain_pct, 1)} unit="%"
                      hint={metricHints.rear} />
            )}
            <Metric label={isGreek ? "Συντελεστής αξιοποίησης (DC)" : "Capacity factor (DC)"} value={nf(m.capacity_factor_pct, 1)} unit="%"
                    hint={metricHints.capacity} />
            <Metric label="POA irradiation" value={nf(m.poa_kwh_m2)} unit="kWh/m²"
                    hint={metricHints.poa} />
            <Metric label="Peak AC" value={nf(m.peak_ac_kw, 2)} unit="kW"
                    hint={metricHints.peak} />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1">Energy by month</Typography>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.4} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={44}
                         label={{ value: "MWh", angle: -90, position: "insideLeft", fontSize: 11 }} />
                  <RechartsTooltip
                    {...rechartsTooltipProps}
                    formatter={(value) => [`${nf(value, 1)} MWh`, isGreek ? "Ενέργεια" : "Energy"]}
                    labelFormatter={(label, payload) => monthlyEnergyTooltipLabel(label, payload, monthly)}
                  />
                  <Bar dataKey="energy_mwh" fill="#1d6fd0" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* A plant on several planes: each plane's share of the modules and of
              the energy, measured per plane by this run. Nothing on one plane. */}
          <OrientationYieldTable rows={estimate?.plant?.orientations} isGreek={isGreek} />

          {/* The optical half, as a table of named deductions.
              This is the form PVsyst's loss diagram takes and the form a
              reviewing engineer reads line by line -- and it is the only way to
              show these steps at all, since eight GWh of sunlight on the
              collector area cannot share an axis with 1.3 GWh of delivered
              energy without flattening every bar below it. */}
          {opticalSteps.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {isGreek ? "Ακτινοβολία που φτάνει στις κυψέλες" : "Sunlight reaching the cells"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {isGreek
                  ? "Από την οριζόντια ακτινοβολία έως το φως που μετατρέπουν οι κυψέλες. Τα ποσοστά αναφέρονται στην ακτινοβολία στο επίπεδο των πλαισίων (POA)."
                  : "From the horizontal resource to the light the cells actually convert. Percentages are of the plane-of-array irradiance."}
              </Typography>
              <Box sx={{ overflowX: "auto", mt: 1 }}>
                <Box component="table" sx={{
                  width: "100%", borderCollapse: "collapse", fontSize: "0.82rem",
                  "& td, & th": {
                    textAlign: "left", padding: "5px 10px 5px 0",
                    borderBottom: "1px solid", borderColor: "divider",
                    verticalAlign: "top",
                  },
                  "& td.n, & th.n": { textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
                }}>
                  <Box component="thead">
                    <Box component="tr">
                      <Box component="th">{isGreek ? "Βήμα" : "Step"}</Box>
                      <Box component="th" className="n">{isGreek ? "Αλλαγή" : "Change"}</Box>
                      <Box component="th" className="n">{isGreek ? "% της POA" : "of POA"}</Box>
                      <Box component="th" className="n">{isGreek ? "Υπόλοιπο" : "Remaining"}</Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {/* The line the whole table is measured from. Without it
                        "from the horizontal resource" names a quantity that
                        appears nowhere, and the first row's "Remaining" has no
                        starting point to be remaining from. */}
                    {opening ? (
                      <Box component="tr">
                        <Box component="td">{yieldLabel(opening.stage, isGreek)}</Box>
                        <Box component="td" className="n" />
                        <Box component="td" className="n" />
                        <Box component="td" className="n">{nf(opening.energy_kwh / 1000, 0)} MWh</Box>
                      </Box>
                    ) : null}
                    {opticalSteps.map((step) => (
                      <Fragment key={step.stage}>
                        <Box component="tr">
                          <Box component="td">{step.displayStage}</Box>
                          <Box component="td" className="n"
                               sx={{ color: step.kind === "gain" ? "success.main" : "text.secondary" }}>
                            {step.kind === "gain"
                              ? `+${nf((step.gain_kwh || 0) / 1000, 0)}`
                              : nf((step.loss_kwh || 0) / 1000, 0)} MWh
                          </Box>
                          <Box component="td" className="n">
                            {step.loss_pct == null ? "—" : `${nf(step.loss_pct, 1)}%`}
                          </Box>
                          <Box component="td" className="n">{nf(step.mwh, 0)} MWh</Box>
                        </Box>
                        {/* Named parts of a step, where the chain measured them.
                            They are shown for the reader; the step's own change
                            above stays the number that counts. */}
                        {(step.components || []).map((part) => (
                          <Box component="tr" key={`${step.stage}-${part.label}`}>
                            <Box component="td" sx={{ pl: 2, color: "text.secondary" }}>
                              <Typography variant="caption">{yieldLabel(part.label, isGreek)}</Typography>
                            </Box>
                            <Box component="td" className="n" />
                            <Box component="td" className="n" sx={{ color: "text.secondary" }}>
                              <Typography variant="caption">{nf(part.pct, 2)}%</Typography>
                            </Box>
                            <Box component="td" className="n" />
                          </Box>
                        ))}
                      </Fragment>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {isGreek ? "Από την ηλιακή ακτινοβολία στην παραδιδόμενη ενέργεια AC" : "From sunlight to delivered AC"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {isGreek
                ? "Κάθε μπάρα δείχνει την ενέργεια που απομένει μετά το αντίστοιχο βήμα, από τη μετατροπή των πλαισίων και μετά. Το βήμα απόδοσης είναι μετατροπή, όχι απώλεια."
                : "Each bar is what remains after that step, from the module's own conversion onward. The efficiency step is a conversion, not a loss."}
            </Typography>
            <Box sx={{ overflowX: "auto", mt: 1,
              "& .recharts-wrapper:focus:not(:focus-visible)": { outline: "none" },
              "& .recharts-wrapper:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
            }}>
            <Box sx={{ height: Math.max(160, ladder.length * 48 + 36), minWidth: 560 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ladder} layout="vertical" margin={{ top: 8, right: 24, bottom: 14, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.4} />
                  <XAxis type="number" tick={{ fontSize: 11 }}
                         label={{ value: "MWh", position: "insideBottomRight", offset: -10, fontSize: 11 }} />
                  <YAxis type="category" dataKey="short" tick={{ fontSize: 12 }} width={242}
                         interval={0} tickLine={false} axisLine={false} />
                  <RechartsTooltip
                    {...rechartsTooltipProps}
                    formatter={(value, _name, item) => {
                      const step = item?.payload;
                      if (step?.kind === "conversion") {
                        return [`${nf(value, 0)} MWh · ${nf(step.efficiency_pct, 2)}% ${isGreek ? "απόδοση πλαισίου" : "module efficiency"}`, step.displayStage];
                      }
                      if (step?.kind === "gain") {
                        return [`${nf(value, 0)} MWh · +${nf(step.gain_kwh / 1000, 0)} MWh ${isGreek ? "από την πίσω όψη" : "from the rear"}`, step.displayStage];
                      }
                      return [`${nf(value, 0)} MWh · ${nf(step?.loss_pct, 2)}%`, step?.displayStage];
                    }}
                    labelFormatter={() => ""}
                  />
                  <Bar dataKey="mwh" radius={[0, 3, 3, 0]} maxBarSize={26}>
                    {ladder.map((step) => (
                      <Cell key={step.stage}
                            fill={step.kind === "gain" ? "#2f8f5b"
                              : step.kind === "conversion" ? "#94a3b8" : "#e08a2e"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            </Box>
          </Box>

          {profile.length ? (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap
                     sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  A day in {(isGreek ? MONTHS_EL : MONTHS)[activeMonth - 1]}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={activeMonth}
                  onChange={(_e, value) => { if (value) setProfileMonth(value); }}
                  sx={{ "& .MuiToggleButton-root": { px: 0.9, py: 0.15, fontSize: 11 } }}
                >
                  {profileMonths.map((row) => (
                    <ToggleButton key={row.month} value={row.month}>
                      {(isGreek ? MONTHS_EL : MONTHS)[row.month - 1]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                The month&apos;s average day, and its sunniest day
                {profileMonths.find((row) => row.month === activeMonth)?.best_day_kwh
                  ? ` (${nf(profileMonths.find((row) => row.month === activeMonth).best_day_kwh)} kWh)`
                  : ""}
                . Hours are the clock on site{offsetLabel ? `, ${offsetLabel}` : ""}, so a
                summer noon here is the noon somebody standing at the plant would see. A flat
                top on the sunny day is the inverter clipping.
              </Typography>
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={profile} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.4} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                    <YAxis tick={{ fontSize: 11 }} width={48}
                           label={{ value: "kW AC", angle: -90, position: "insideLeft", fontSize: 11 }} />
                    <RechartsTooltip
                      {...rechartsTooltipProps}
                      formatter={(value, name, item) => {
                        if (name === "Sunniest day" || name === "Πιο ηλιόλουστη ημέρα") return [`${nf(value, 1)} kW`, name];
                        const poa = item?.payload?.poa;
                        return [
                          `${nf(value, 1)} kW${poa == null ? "" : ` · ${nf(poa)} W/m² POA`}`,
                          name,
                        ];
                      }}
                      labelFormatter={(label) => `${label}:00`}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="mean" name={isGreek ? "Μέση ημέρα" : "Average day"}
                          stroke="#1d6fd0" fill="#1d6fd0" fillOpacity={0.22} strokeWidth={2} />
                    <Line type="monotone" dataKey="best" name={isGreek ? "Πιο ηλιόλουστη ημέρα" : "Sunniest day"}
                          stroke="#e08a2e" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : null}

          {estimate?.heatmap?.values?.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Output through the year
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Hour of day up, day of year across. A flat summer top means the inverter
                is clipping; a bitten left edge means something is shading the mornings.
              </Typography>
              <Heatmap values={estimate.heatmap.values} peak={m.peak_ac_kw} />
            </Box>
          ) : null}

          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {estimate?.weather?.label ? (
              <Chip size="small" variant="outlined" label={`Weather: ${estimate.weather.label}`} />
            ) : null}
            {estimate?.module ? (
              <Chip size="small" variant="outlined" label={estimate.module} />
            ) : null}
            {estimate?.inverter ? (
              <Chip size="small" variant="outlined" label={estimate.inverter} />
            ) : null}
            <Tooltip arrow title={timeBaseTooltip(estimate?.time_base, isGreek)}>
              <Chip size="small" variant="outlined" sx={{ cursor: "help" }}
                    label={`${nf(estimate?.time_base?.rows)} ${
                      isGreek ? "ωριαίες σειρές · ώρα έργου"
                               : "hourly rows · site clock"
                    }${offsetLabel ? ` (${offsetLabel})` : ""}`} />
            </Tooltip>
          </Stack>
        </>
      ) : null}
    </Stack>
    </PvLoadingRegion>
  );
}
